import { RulesSection } from "./components/RulesSection";
import { Button, Flex, Grid } from "src/elements";
import { SliderCard } from "./components/SliderCard";
import { useState } from "react";

interface Props {
  isDisabled?: boolean;

  // Mudmap Optimization Options toggle
  mudmapOptimizationEnabled: boolean;
  setMudmapOptimizationEnabled: (value: boolean) => void;

  // Penalty multipliers
  totalSpaceSlack: number;
  setTotalSpaceSlack: (value: number) => void;

  cdt0SectionsSpanning: number;
  setCdt0SectionsSpanning: (value: number) => void;

  cdt0ShelvesSpanning: number;
  setCdt0ShelvesSpanning: (value: number) => void;

  cdt2SectionsSpanning: number;
  setCdt2SectionsSpanning: (value: number) => void;

  cdt2ShelvesSpanning: number;
  setCdt2ShelvesSpanning: (value: number) => void;

  cdt0RoundingSlack: number;
  setCdt0RoundingSlack: (value: number) => void;

  cdt2RoundingSlack: number;
  setCdt2RoundingSlack: (value: number) => void;

  cdt1BreakingSequencing: number;
  setCdt1BreakingSequencing: (value: number) => void;

  cdt2BreakingSequencing: number;
  setCdt2BreakingSequencing: (value: number) => void;

  cdtSpaceMixSlack: number;
  setCdtSpaceMixSlack: (value: number) => void;

  cdt0ShelvesMisaligned: number;
  setCdt0ShelvesMisaligned: (value: number) => void;

  itemWidthSatisfied: number;
  setItemWidthSatisfied: (value: number) => void;

  cdt0ShelfSpaceMisalignment: number;
  setCdt0ShelfSpaceMisalignment: (value: number) => void;

  cdt2ShelfSpaceMisalignment: number;
  setCdt2ShelfSpaceMisalignment: (value: number) => void;

  verticalBlockShelfPresence: number;
  setVerticalBlockShelfPresence: (value: number) => void;

  cdt0CellXMisalignment: number;
  setCdt0CellXMisalignment: (value: number) => void;

  cdt2CellXMisalignment: number;
  setCdt2CellXMisalignment: (value: number) => void;

  onReset: () => void;
}

export const MudmapOptimizationOptions = ({
  isDisabled,
  mudmapOptimizationEnabled,
  setMudmapOptimizationEnabled,
  totalSpaceSlack,
  setTotalSpaceSlack,
  cdt0SectionsSpanning,
  setCdt0SectionsSpanning,
  cdt0ShelvesSpanning,
  setCdt0ShelvesSpanning,
  cdt2SectionsSpanning,
  setCdt2SectionsSpanning,
  cdt2ShelvesSpanning,
  setCdt2ShelvesSpanning,
  cdt0RoundingSlack,
  setCdt0RoundingSlack,
  cdt2RoundingSlack,
  setCdt2RoundingSlack,
  cdt1BreakingSequencing,
  setCdt1BreakingSequencing,
  cdt2BreakingSequencing,
  setCdt2BreakingSequencing,
  cdtSpaceMixSlack,
  setCdtSpaceMixSlack,
  cdt0ShelvesMisaligned,
  setCdt0ShelvesMisaligned,
  itemWidthSatisfied,
  setItemWidthSatisfied,
  cdt0ShelfSpaceMisalignment,
  setCdt0ShelfSpaceMisalignment,
  cdt2ShelfSpaceMisalignment,
  setCdt2ShelfSpaceMisalignment,
  verticalBlockShelfPresence,
  setVerticalBlockShelfPresence,
  cdt0CellXMisalignment,
  setCdt0CellXMisalignment,
  cdt2CellXMisalignment,
  setCdt2CellXMisalignment,
  onReset,
}: Props) => {
  const [selectedCdtSectionSpan, setSelectedCdtSectionSpan] = useState<"CDT0" | "CDT1_2">("CDT0");
  const [selectedCdtOrdering, setSelectedCdtOrdering] = useState<"CDT1" | "CDT2">("CDT2");
  const [selectedCdtShelfSpan, setSelectedCdtShelfSpan] = useState<"CDT0" | "CDT1_2">("CDT0");
  const [selectedShelfRounding, setSelectedShelfRounding] = useState<"CDT0" | "CDT1_2">("CDT1_2");
  const [selectedShelfSpaceAlignment, setSelectedShelfSpaceAlignment] = useState<"CDT0" | "CDT1_2">(
    "CDT0",
  );
  const [selectedCellXAlignment, setSelectedCellXAlignment] = useState<"CDT0" | "CDT1_2">("CDT0");

  return (
    <Grid
      width="100%"
      gap="20px"
      areas={`
        "mudmap-section mudmap-section"
        "cdt-section-span cdt-shelf-span"
        "cdt-ordering shelf-rounding"
        "empty-space shelf-alignment"
        "cdt-space-mix item-width"
        "cdt-shelf-space-alignment cdt-cell-x-alignment"
        "vertical-expansion-reward vertical-expansion-reward"
        "reset reset"
      `}
      columns="1fr 1fr"
    >
      <RulesSection
        title="Mudmap Optimization Options"
        area="mudmap-section"
        toggleValue={mudmapOptimizationEnabled}
        setToggleValue={setMudmapOptimizationEnabled}
        isDisabled={isDisabled}
      />
      <SliderCard
        area="cdt-section-span"
        title="CDT Section Span"
        description={
          selectedCdtSectionSpan === "CDT0"
            ? "Allow CDT 0 nodes to span multiple sections or disallow this."
            : "Allow CDT 1/CDT 2 nodes to span multiple sections or disallow this."
        }
        parentRadioConfig={{
          options: [
            { label: "CDT 0", value: "CDT0" },
            { label: "CDT 1/2", value: "CDT1_2" },
          ],
          selection: selectedCdtSectionSpan,
          onSelect: (value) => setSelectedCdtSectionSpan(value as "CDT0" | "CDT1_2"),
        }}
        sliderValue={
          selectedCdtSectionSpan === "CDT0" ? cdt0SectionsSpanning : cdt2SectionsSpanning
        }
        onSliderChange={
          selectedCdtSectionSpan === "CDT0" ? setCdt0SectionsSpanning : setCdt2SectionsSpanning
        }
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="cdt-shelf-span"
        title="CDT Shelf Span"
        description={
          selectedCdtShelfSpan === "CDT0"
            ? "Allow CDT 0 nodes to span multiple shelves or disallow this."
            : "Allow CDT 1/CDT 2 nodes to span multiple shelves or disallow this."
        }
        parentRadioConfig={{
          options: [
            { label: "CDT 0", value: "CDT0" },
            { label: "CDT 1/2", value: "CDT1_2" },
          ],
          selection: selectedCdtShelfSpan,
          onSelect: (value) => setSelectedCdtShelfSpan(value as "CDT0" | "CDT1_2"),
        }}
        sliderValue={selectedCdtShelfSpan === "CDT0" ? cdt0ShelvesSpanning : cdt2ShelvesSpanning}
        onSliderChange={
          selectedCdtShelfSpan === "CDT0" ? setCdt0ShelvesSpanning : setCdt2ShelvesSpanning
        }
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="cdt-ordering"
        title="CDT Ordering"
        description={
          selectedCdtOrdering === "CDT1"
            ? "Allow CDT 1 priority to be broken or disallow this."
            : "Allow CDT 2 priority to be broken or disallow this."
        }
        parentRadioConfig={{
          options: [
            { label: "CDT 1", value: "CDT1" },
            { label: "CDT 2", value: "CDT2" },
          ],
          selection: selectedCdtOrdering,
          onSelect: (value) => setSelectedCdtOrdering(value as "CDT1" | "CDT2"),
        }}
        sliderValue={
          selectedCdtOrdering === "CDT1" ? cdt1BreakingSequencing : cdt2BreakingSequencing
        }
        onSliderChange={
          selectedCdtOrdering === "CDT1" ? setCdt1BreakingSequencing : setCdt2BreakingSequencing
        }
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="shelf-rounding"
        title="Shelf Rounding"
        description={
          selectedShelfRounding === "CDT0"
            ? "Allow CDT 0 nodes to occupy only part of a shelf they are placed on or disallow this."
            : "Allow CDT 1/CDT 2 nodes to occupy only part of a shelf they are placed on or disallow this."
        }
        parentRadioConfig={{
          options: [
            { label: "CDT 0", value: "CDT0" },
            { label: "CDT 1/2", value: "CDT1_2" },
          ],
          selection: selectedShelfRounding,
          onSelect: (value) => setSelectedShelfRounding(value as "CDT0" | "CDT1_2"),
        }}
        sliderValue={selectedShelfRounding === "CDT0" ? cdt0RoundingSlack : cdt2RoundingSlack}
        onSliderChange={
          selectedShelfRounding === "CDT0" ? setCdt0RoundingSlack : setCdt2RoundingSlack
        }
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="empty-space"
        title="Empty Space"
        description="Allow empty space in the mudmap or disallow this."
        sliderValue={totalSpaceSlack}
        onSliderChange={setTotalSpaceSlack}
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="shelf-alignment"
        title="CDT 0 Shelf Alignment"
        description="Allow variant shelves to be misaligned when a variant occupies two consecutive sections or disallow this."
        sliderValue={cdt0ShelvesMisaligned}
        onSliderChange={setCdt0ShelvesMisaligned}
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="cdt-space-mix"
        title="CDT Space Mix"
        description="Allow deviation from target space mix (sales / request-based) or disallow this."
        sliderValue={cdtSpaceMixSlack}
        onSliderChange={setCdtSpaceMixSlack}
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="item-width"
        title="Item Width Satisfied"
        description="Reward each individual item width requirement that is satisfied within the allocated CDT block space on a shelf."
        sliderValue={itemWidthSatisfied}
        onSliderChange={setItemWidthSatisfied}
        leftLabel="No Reward"
        rightLabel="High Reward"
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="cdt-shelf-space-alignment"
        title="CDT Shelf Space Alignment"
        description={
          selectedShelfSpaceAlignment === "CDT0"
            ? "Allow the same CDT 0 block to receive different space allocations across adjacent shelves within the same section, resulting in a non-rectangular shape, or disallow this."
            : "Allow the same CDT 1/CDT 2 block to receive different space allocations across adjacent shelves within the same section, resulting in a non-rectangular shape, or disallow this."
        }
        parentRadioConfig={{
          options: [
            { label: "CDT 0", value: "CDT0" },
            { label: "CDT 1/2", value: "CDT1_2" },
          ],
          selection: selectedShelfSpaceAlignment,
          onSelect: (value) => setSelectedShelfSpaceAlignment(value as "CDT0" | "CDT1_2"),
        }}
        sliderValue={
          selectedShelfSpaceAlignment === "CDT0"
            ? cdt0ShelfSpaceMisalignment
            : cdt2ShelfSpaceMisalignment
        }
        onSliderChange={
          selectedShelfSpaceAlignment === "CDT0"
            ? setCdt0ShelfSpaceMisalignment
            : setCdt2ShelfSpaceMisalignment
        }
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="cdt-cell-x-alignment"
        title="CDT Cell X Alignment"
        description={
          selectedCellXAlignment === "CDT0"
            ? "Allow local cells of the same CDT 0 block to be misaligned between two consecutive shelves in the same section and the same shelf rank across consecutive sections, or disallow this."
            : "Allow local cells of the same CDT 1/CDT 2 block to be misaligned between two consecutive shelves in the same section and the same shelf rank across consecutive sections, or disallow this."
        }
        parentRadioConfig={{
          options: [
            { label: "CDT 0", value: "CDT0" },
            { label: "CDT 1/2", value: "CDT1_2" },
          ],
          selection: selectedCellXAlignment,
          onSelect: (value) => setSelectedCellXAlignment(value as "CDT0" | "CDT1_2"),
        }}
        sliderValue={
          selectedCellXAlignment === "CDT0" ? cdt0CellXMisalignment : cdt2CellXMisalignment
        }
        onSliderChange={
          selectedCellXAlignment === "CDT0" ? setCdt0CellXMisalignment : setCdt2CellXMisalignment
        }
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <SliderCard
        area="vertical-expansion-reward"
        title="Vertical Expansion Reward"
        description="A higher value gives the model a stronger reward when a CDT block expands onto an additional shelf within the same section."
        sliderValue={verticalBlockShelfPresence}
        onSliderChange={setVerticalBlockShelfPresence}
        leftLabel="No Reward"
        rightLabel="High Reward"
        isDisabled={isDisabled || !mudmapOptimizationEnabled}
      />

      <Flex gridArea="reset" justify="center">
        <Button
          color="yellow"
          onClick={onReset}
          isDisabled={isDisabled || !mudmapOptimizationEnabled}
        >
          Reset all Optimization Options
        </Button>
      </Flex>
    </Grid>
  );
};
